/**
 * 任务存储管理
 */

import fs from 'fs-extra';
import path from 'path';
import { Task, TaskPriority, TaskStatus } from '../types';
import { ExtendedTask, isExtendedTask, toExtendedTask } from '../types/extended-task';
import { logger } from '../utils/logger';
import { webhookService } from '../services/webhook-service';
import { syncManager } from '../services/sync-manager';
import { TodoExporter } from '../utils/todo-exporter';
import { HookManager, HookType } from './hooks';

export class TaskStore {
  private hookManager?: HookManager;
  private storePath: string;
  private tasks: ExtendedTask[] = [];
  private initialized: boolean = false;
  private projectRoot: string;
  private todoExporter: TodoExporter;
  private dataVersion: string = '2.0.0';

  constructor(projectRoot: string = process.cwd(), hookManager?: HookManager) {
    this.projectRoot = projectRoot;
    this.storePath = path.join(projectRoot, '.workflow/tasks.json');
    this.todoExporter = new TodoExporter({
      projectName: path.basename(projectRoot),
      includeStats: true,
      includeTimeline: true,
      locale: 'zh'
    });
    this.hookManager = hookManager;
  }
  
  // 设置 HookManager（用于延迟注入）
  setHookManager(hookManager: HookManager): void {
    this.hookManager = hookManager;
  }

  async initialize(): Promise<void> {
    if (!this.initialized) {
      await this.loadTasks();
      this.initialized = true;
    }
  }

  private async loadTasks(): Promise<void> {
    try {
      if (await fs.pathExists(this.storePath)) {
        const fileData = await fs.readJson(this.storePath);
        
        // 处理新的数据格式 { tasks: [...], version: '2.0.0' }
        let rawTasks: any[];
        if (Array.isArray(fileData)) {
          // 旧格式，需要转换
          rawTasks = fileData;
          logger.info('Loading legacy task format, will migrate to ExtendedTask');
        } else if (fileData.tasks && Array.isArray(fileData.tasks)) {
          // 新格式
          rawTasks = fileData.tasks;
          this.dataVersion = fileData.version || '2.0.0';
        } else {
          rawTasks = [];
        }
        
        // 确保日期字段被正确解析，并转换为 ExtendedTask
        this.tasks = rawTasks.map((task: any) => {
          const baseTask: Task = {
            ...task,
            createdAt: task.createdAt ? new Date(task.createdAt) : new Date(),
            updatedAt: task.updatedAt ? new Date(task.updatedAt) : new Date(),
            completedAt: task.completedAt ? new Date(task.completedAt) : undefined,
            dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
            startedAt: task.startedAt ? new Date(task.startedAt) : undefined,
            pausedAt: task.pausedAt ? new Date(task.pausedAt) : undefined,
            cancelledAt: task.cancelledAt ? new Date(task.cancelledAt) : undefined
          };
          
          // 如果已经是 ExtendedTask，直接返回；否则转换
          return isExtendedTask(baseTask) ? baseTask as ExtendedTask : toExtendedTask(baseTask);
        });
      } else {
        // 如果文件不存在，创建一个空的任务列表
        logger.info('tasks.json not found, creating empty task list');
        this.tasks = [];
        await this.saveTasks(this.tasks);
      }
    } catch (error) {
      logger.error('Failed to load tasks:', error);
      this.tasks = [];
    }
  }

  private async saveTasks(tasks: ExtendedTask[]): Promise<void> {
    await fs.ensureDir(path.dirname(this.storePath));
    
    // 使用新的数据格式保存
    const dataToSave = {
      tasks: tasks,
      version: this.dataVersion,
      migrated: true,
      updatedAt: new Date().toISOString()
    };
    
    await fs.writeJson(this.storePath, dataToSave, { spaces: 2 });
    this.tasks = tasks;
    
    // 自动导出 TODO.md
    await this.exportTodoMd(tasks);
  }

  private async exportTodoMd(tasks: ExtendedTask[]): Promise<void> {
    try {
      const todoPath = path.join(this.projectRoot, '.workflow/TODO.md');
      await this.todoExporter.export(tasks, todoPath);
      logger.debug('TODO.md exported successfully');
    } catch (error) {
      logger.error('Failed to export TODO.md:', error);
    }
  }

  async getCurrentTask(): Promise<ExtendedTask | null> {
    await this.initialize();
    return this.tasks.find(t => t.isCurrent) || null;
  }

  async setCurrentTask(taskId: string | null): Promise<void> {
    await this.initialize();
    const tasks = [...this.tasks];
    const now = new Date();
    
    // 将当前任务暂停
    const currentTask = tasks.find(t => t.isCurrent);
    if (currentTask && currentTask.id !== taskId) {
      currentTask.isCurrent = false;
      if (currentTask.status === 'in_progress') {
        currentTask.status = 'paused';
        currentTask.pausedAt = now;
        currentTask.pauseReason = 'Switched to another task';
        // 计算暂停时长
        if (currentTask.startedAt) {
          const duration = now.getTime() - new Date(currentTask.startedAt).getTime();
          currentTask.pausedDuration = (currentTask.pausedDuration || 0) + duration;
        }
      }
    }
    
    // 设置新的当前任务
    if (taskId) {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        task.isCurrent = true;
        task.updatedAt = now;
        
        // 根据之前的状态设置新状态
        if (task.status === 'todo' || task.status === 'paused') {
          task.status = 'in_progress';
          if (!task.startedAt) {
            task.startedAt = now;
          }
        }
      }
      
      // 触发 start hook
      if (task && task.status === 'in_progress' && this.hookManager) {
        await this.hookManager.onTaskStart(task);
      }
    }
    
    await this.saveTasks(tasks);
  }

  async addTask(task: Partial<Task> | Partial<ExtendedTask>): Promise<ExtendedTask> {
    await this.initialize();
    const tasks = await this.getTasks();
    
    // 支持扩展任务类型
    const isExtended = isExtendedTask(task as any);
    const baseTask: Task = {
      id: Date.now().toString(),
      content: task.content || '',
      status: task.status || 'todo',
      priority: task.priority || 'medium',
      position: tasks.length,
      createdAt: new Date(),
      updatedAt: new Date(),
      ...task
    };
    
    // Handle tags from metadata if present (for PRD parsing)
    if ((task as any).metadata?.tags && !baseTask.tags) {
      baseTask.tags = (task as any).metadata.tags;
    }
    
    // 转换为 ExtendedTask
    const newTask: ExtendedTask = isExtended 
      ? { ...baseTask, ...task } as ExtendedTask
      : toExtendedTask(baseTask);
    
    tasks.push(newTask);
    await this.saveTasks(tasks);
    
    // 使用智能同步管理器
    await syncManager.syncTaskChange('task.create', { task: newTask }, {
      source: 'cli',
      isUserAction: true
    });
    
    // 触发 hook
    if (this.hookManager) {
      await this.hookManager.onTaskCreate(newTask);
    }
    
    return newTask;
  }

  async getTasks(): Promise<ExtendedTask[]> {
    await this.initialize();
    return [...this.tasks]; // 返回副本以防止外部修改
  }

  async getTaskById(id: string): Promise<ExtendedTask | undefined> {
    await this.initialize();
    return this.tasks.find(t => t.id === id);
  }
  
  // 新增：获取扩展任务
  async getExtendedTaskById(id: string): Promise<ExtendedTask | undefined> {
    await this.initialize();
    const task = this.tasks.find(t => t.id === id);
    return task ? toExtendedTask(task) : undefined;
  }

  async updateTask(id: string, updates: Partial<Task> | Partial<ExtendedTask>): Promise<ExtendedTask | undefined> {
    await this.initialize();
    const tasks = await this.getTasks();
    const index = tasks.findIndex(t => t.id === id);
    
    if (index === -1) return undefined;
    
    const previousTask = { ...tasks[index] };
    const existingTask = tasks[index];
    
    // 保留现有的扩展字段
    const updatedTask = isExtendedTask(existingTask) 
      ? { ...existingTask, ...updates, updatedAt: new Date() }
      : { ...existingTask, ...updates, updatedAt: new Date() };
    
    tasks[index] = updatedTask;
    
    await this.saveTasks(tasks);
    
    // 使用智能同步管理器
    await syncManager.syncTaskChange('task.update', { task: updatedTask, updates }, {
      source: 'cli',
      isUserAction: true
    });
    
    // 触发 hook
    if (this.hookManager) {
      await this.hookManager.onTaskUpdate(updatedTask, updates);
      
      // 特殊状态变更 hooks
      if (updates.status === 'done' && previousTask.status !== 'done') {
        await this.hookManager.onTaskComplete(updatedTask);
      } else if (updates.status === 'in_progress' && previousTask.status !== 'in_progress') {
        await this.hookManager.onTaskStart(updatedTask);
      }
    }
    
    return updatedTask;
  }

  async deleteTask(id: string): Promise<boolean> {
    await this.initialize();
    const tasks = await this.getTasks();
    const index = tasks.findIndex(t => t.id === id);
    
    if (index === -1) return false;
    
    tasks.splice(index, 1);
    // 重新计算位置
    tasks.forEach((task, i) => {
      task.position = i;
    });
    
    await this.saveTasks(tasks);
    
    // 使用智能同步管理器
    await syncManager.syncTaskChange('task.delete', { taskId: id }, {
      source: 'cli',
      isUserAction: true
    });
    
    // 触发 hook
    if (this.hookManager) {
      await this.hookManager.trigger(HookType.TASK_DELETE, { id });
    }
    
    return true;
  }

  async getTasksByStatus(status: TaskStatus): Promise<ExtendedTask[]> {
    await this.initialize();
    return this.tasks.filter(t => t.status === status);
  }

  async getTasksByPriority(priority: TaskPriority): Promise<ExtendedTask[]> {
    await this.initialize();
    return this.tasks.filter(t => t.priority === priority);
  }

  async getTasksByTags(tags: string[], mode: 'any' | 'all' = 'any'): Promise<ExtendedTask[]> {
    await this.initialize();
    return this.tasks.filter(task => {
      if (!task.tags || task.tags.length === 0) return false;
      
      if (mode === 'any') {
        return tags.some(tag => task.tags!.includes(tag));
      } else {
        return tags.every(tag => task.tags!.includes(tag));
      }
    });
  }

  async searchTasks(query: string): Promise<ExtendedTask[]> {
    await this.initialize();
    const lowerQuery = query.toLowerCase();
    
    return this.tasks.filter(task =>
      task.content.toLowerCase().includes(lowerQuery) ||
      (task.tags && task.tags.some(tag => tag.toLowerCase().includes(lowerQuery))) ||
      (task.section && task.section.toLowerCase().includes(lowerQuery))
    );
  }

  async reorderTasks(taskIds: string[]): Promise<void> {
    await this.initialize();
    const tasks = await this.getTasks();
    const reorderedTasks: Task[] = [];
    
    // 首先添加指定顺序的任务
    for (const id of taskIds) {
      const task = tasks.find(t => t.id === id);
      if (task) {
        task.position = reorderedTasks.length;
        reorderedTasks.push(task);
      }
    }
    
    // 添加未指定的任务
    for (const task of tasks) {
      if (!taskIds.includes(task.id)) {
        task.position = reorderedTasks.length;
        reorderedTasks.push(task);
      }
    }
    
    await this.saveTasks(reorderedTasks);
    
    // 使用智能同步管理器
    await syncManager.syncTaskChange('task.reorder', { taskIds }, {
      source: 'cli',
      isUserAction: true
    });
  }

  async bulkUpdate(updates: Array<{ id: string; updates: Partial<Task> | Partial<ExtendedTask> }>): Promise<number> {
    await this.initialize();
    const tasks = await this.getTasks();
    let updatedCount = 0;
    
    for (const { id, updates: taskUpdates } of updates) {
      const index = tasks.findIndex(t => t.id === id);
      if (index !== -1) {
        const existingTask = tasks[index];
        // 保留扩展字段
        tasks[index] = isExtendedTask(existingTask)
          ? { ...existingTask, ...taskUpdates, updatedAt: new Date() }
          : { ...existingTask, ...taskUpdates, updatedAt: new Date() };
        updatedCount++;
      }
    }
    
    if (updatedCount > 0) {
      await this.saveTasks(tasks);
      
      // 使用智能同步管理器（批量操作）
      await syncManager.syncTaskChange('task.bulk', { 
        operation: 'update',
        taskIds: updates.map(u => u.id),
        count: updatedCount 
      }, {
        source: 'cli',
        isUserAction: true
      });
    }
    
    return updatedCount;
  }

  async clearCompleted(): Promise<number> {
    await this.initialize();
    const tasks = await this.getTasks();
    const initialCount = tasks.length;
    const remainingTasks = tasks.filter(t => t.status !== 'done' && t.status !== 'archived');
    
    // 重新计算位置
    remainingTasks.forEach((task, i) => {
      task.position = i;
    });
    
    await this.saveTasks(remainingTasks);
    
    const deletedCount = initialCount - remainingTasks.length;
    if (deletedCount > 0) {
      // 使用智能同步管理器（批量操作）
      const deletedTaskIds = tasks
        .filter(t => t.status === 'done' || t.status === 'archived')
        .map(t => t.id);
      
      await syncManager.syncTaskChange('task.bulk', {
        operation: 'clear-completed',
        taskIds: deletedTaskIds,
        count: deletedCount
      }, {
        source: 'cli',
        isUserAction: true
      });
    }
    
    return deletedCount;
  }
  
  // 新增：获取与 PRD 关联的任务
  async getTasksByPrdId(prdId: string): Promise<ExtendedTask[]> {
    await this.initialize();
    return this.tasks
      .filter(task => isExtendedTask(task) && (task as ExtendedTask).prdId === prdId)
      .map(task => toExtendedTask(task));
  }
  
  // 新增：获取与设计文档关联的任务
  async getTasksByDesignId(designId: string): Promise<ExtendedTask[]> {
    await this.initialize();
    return this.tasks
      .filter(task => isExtendedTask(task) && (task as ExtendedTask).designId === designId)
      .map(task => toExtendedTask(task));
  }
  
  // 新增：获取与工作流关联的任务
  async getTasksByWorkflowId(workflowId: string): Promise<ExtendedTask[]> {
    await this.initialize();
    return this.tasks
      .filter(task => isExtendedTask(task) && (task as ExtendedTask).workflowId === workflowId)
      .map(task => toExtendedTask(task));
  }
  
  // 新增：获取分配给特定 Agent 的任务
  async getTasksByAgent(agentName: string): Promise<ExtendedTask[]> {
    await this.initialize();
    return this.tasks
      .filter(task => {
        if (!isExtendedTask(task)) return false;
        const extTask = task as ExtendedTask;
        return extTask.agentAssignments && (
          extTask.agentAssignments.primary === agentName ||
          extTask.agentAssignments.reviewers?.includes(agentName) ||
          extTask.agentAssignments.consultants?.includes(agentName)
        );
      })
      .map(task => toExtendedTask(task));
  }
  
  // 新增：获取包含特定技术栈的任务
  async getTasksByTechStack(tech: string): Promise<ExtendedTask[]> {
    await this.initialize();
    return this.tasks
      .filter(task => {
        if (!isExtendedTask(task)) return false;
        const extTask = task as ExtendedTask;
        return extTask.techStack?.includes(tech);
      })
      .map(task => toExtendedTask(task));
  }
}